import React, { useEffect, useRef, useState } from "react";
import EzTable from "../../../common/EzTable";
import { EzToast, TOAST_SEVERITY } from "../../../common/EzToast";
import EzNotFound from "../../../common/EzNotFound";
import { Box, Container } from "@mui/material";
import Breadcrumb from "../../../common/Breadcrumb";
import { Button } from "primereact/button";
import { ProgressBar } from "primereact/progressbar";
import { useStyles } from "./style";
import { RateAdminBreadcrumb } from "./CreateRateAdminRates/constant";
import { printTableData } from "../../../utils/printUtils";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ViewInlineRateAdminRates from "./ViewInlineRateAdminRates";
import { FILTER } from "../../../services/common/urlService";
import { DATE_STRATEGY, formatDate } from "../../../utils/date";

const getIconPath = (imgPathDelta = "/no-record.png") =>
  process.env.PUBLIC_URL + imgPathDelta;
const RateAdminRates = (props) => {
  const classes = useStyles();
  const {
    fetchRateAdminRatess,
    tableData,
    setDeleteConfirmationPopupVisibility,
    shouldOpenDeleteConfirmationPopup,
    exportRateAdminRates,
    isPageLevelError,
    paginationInfo,
    isLoading,
    screenPermissions,
    handleCreateRateAdminRates,
    editRateAdminRatesRoute,
    copyRateAdminRatesRoute,
    viewRateAdminRatesRoute,
    deleteRateAdminRates,
    isRateAdminRatesDeleteSuccess,
    isRateAdminRatesDeleteError,
    savedTableState,
    onTableStateChange,
    fetchRateAdminRatesById,
    formFieldValueMap,
    isRateAdminRatesDetailSuccess,
    // Dropdown data for inline view
    RateCodeDD,
    locationDD,
    vehicleCategoryDD,
    RateOptionsDD,
    RateNetworkDD,
    // Additional props for inline view functionality
    fetchRateAdminRatesRateAdjDetails,
    createRateAdminRateAdjustment,
    TestRateAdminRates,
    fetchExternalNetworkDD,
    fetchExternalRateTest,
    clearExternalRateTest,
    shouldBlockNavigation,
    rateAdjustmentlist,
    RateTest,
    ExternalNetworkDD,
    ExternalRateTest,
    isExternalRateTestSuccess,
    isExternalRateTestError,
    isRateAdminRateAdjSuccess,
    isRateAdminRateAdjListSuccess,
    editRateAdminRates,
    isRateAdminRatesEditSuccess,
    isRateAdminRatesEditError,
    // Props for location-based categories
    fetchVehicleCategoryByLocation,
    vehicleCategoryByLocationDD,
    isVehicleCategoryByLocationSuccess,
    // Props for revenue preview
    fetchRevenuePreview,
    revenuePreviewData,
    isRevenuePreviewSuccess,
  } = props;

  const [isCopy, setIsCopy] = useState(false);
  const [isPrintError, setIsPrintError] = useState(false);
  const [expandedRows, setExpandedRows] = useState(savedTableState?.expandedRows || null);
  const [rateDetails, setRateDetails] = useState({});
  const [pendingFetchId, setPendingFetchId] = useState(null);

  const renderProgressBar = () => {
    return <ProgressBar mode="indeterminate" style={{ height: "4px" }} />;
  };
  const convertToTSV = (data) => {
    const headers = data.columns.map((item) => item.colLabel).join("\t");
    //const headers = Object.keys(data.rows[0]).join('\t');
    const rows = data.rows
      .map((row) => Object.values(row).join("\t"))
      .join("\n");
    return `${headers}\n${rows}`;
  };

  const copyToClipboard = () => {
    const csv = convertToTSV(tableData);
    navigator.clipboard
      .writeText(csv)
      .then(() => setIsCopy(true))
      .catch((err) => setIsCopy(true));
  };
  useEffect(() => {
    setTimeout(() => setIsCopy(false), 1000);
  }, [isCopy]);
  useEffect(() => {
    if (isPrintError) {
      const timer = setTimeout(() => setIsPrintError(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isPrintError]);

  // Update rateDetails when formFieldValueMap is updated from Redux
  useEffect(() => {
    if (pendingFetchId && isRateAdminRatesDetailSuccess && formFieldValueMap) {
      if (formFieldValueMap.id === pendingFetchId) {
        setRateDetails(prev => ({ ...prev, [pendingFetchId]: formFieldValueMap }));
        setPendingFetchId(null);
        
        // Call fetchRevenuePreview when row is expanded and details are loaded
        if (fetchRevenuePreview) {
          const payload = {
            rateId: formFieldValueMap.id,
            rateBaseDTO: [
              { fkRatePlanId: 1, rate: parseFloat(formFieldValueMap.dailybaseRate) || 0, miles: 0 },
              { fkRatePlanId: 2, rate: parseFloat(formFieldValueMap.weekendbaseRate) || 0, miles: 0 },
              { fkRatePlanId: 3, rate: parseFloat(formFieldValueMap.weeklybaseRate) || 0, miles: 0 },
              { fkRatePlanId: 4, rate: parseFloat(formFieldValueMap.monthlybaseRate) || 0, miles: 0 }
            ]
          };
          fetchRevenuePreview(payload);
        }
      }
    }
  }, [formFieldValueMap, isRateAdminRatesDetailSuccess, pendingFetchId, fetchRevenuePreview]);

  // Update rateDetails when formFieldValueMap changes after edit success
  useEffect(() => {
    if (formFieldValueMap && formFieldValueMap.id && expandedRows) {
      const expandedIds = Object.keys(expandedRows);
      if (expandedIds.includes(String(formFieldValueMap.id))) {
        setRateDetails(prev => ({ ...prev, [formFieldValueMap.id]: formFieldValueMap }));
      }
    }
  }, [formFieldValueMap, expandedRows]);

  // Helper function to build fetch params from saved state (preserves filters)
  const buildFetchParamsFromSavedState = (state) => {
    if (!state) return { sortField: 'id' };
    
    // Helper to check if a string looks like an ISO date
    const isISODateString = (str) => {
      if (typeof str !== 'string') return false;
      return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str);
    };
    
    // Helper to format date values to YYYY-MM-DD
    const formatFilterValue = (value) => {
      if (value instanceof Date) {
        return formatDate(value, DATE_STRATEGY.DATE_ISO);
      }
      if (isISODateString(value)) {
        return formatDate(new Date(value), DATE_STRATEGY.DATE_ISO);
      }
      return value;
    };
    
    // Build filter params in the format expected by the API
    const filters = [];
    if (state.filters) {
      Object.keys(state.filters).forEach(key => {
        if (key !== 'global' && state.filters[key]?.value !== null && state.filters[key]?.value !== undefined) {
          const filterValue = state.filters[key].value;
          filters.push({
            filterField: state.filters[key].filterKey || key,
            matchStrategy: state.filters[key].matchStrategy || FILTER.MATCH_STRATEGY.CONTAINS,
            query: formatFilterValue(filterValue)
          });
        }
      });
    }
    
    return {
      sortField: state.sortField || 'id',
      sortOrder: state.sortOrder,
      pageNumber: state.page || 0,
      pageSize: state.rows || 10,
      filters: filters.length > 0 ? filters : undefined
    };
  };

  // Refresh grid data after successful edit
  useEffect(() => {
    if (isRateAdminRatesEditSuccess) {
      // Refresh the grid data with preserved filters and pagination
      if (fetchRateAdminRatess) {
        const params = buildFetchParamsFromSavedState(savedTableState);
        fetchRateAdminRatess(params);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRateAdminRatesEditSuccess]);

  // Fetch details for previously expanded rows when component mounts
  useEffect(() => {
    if (savedTableState?.expandedRows) {
      const expandedIds = Object.keys(savedTableState.expandedRows);
      for (const rowId of expandedIds) {
        const numericId = parseInt(rowId, 10);
        if (numericId && !rateDetails[numericId]) {
          setPendingFetchId(numericId);
          fetchRateAdminRatesById({ id: numericId });
          if (fetchRateAdminRatesRateAdjDetails) {
            fetchRateAdminRatesRateAdjDetails({ id: numericId });
          }
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount

  const handlePrint = () => {
    const success = printTableData({
      title: "Rates",
      columns: tableData?.columns || [],
      rows: tableData?.rows || [],
    });
    if (!success) {
      setIsPrintError(true);
    }
  };

  const [areFiltersVisible, toggleFilterState] = useState(true);
  const renderPageHeaderSection = () => {
    return (
      <div className="make-relative sub-header ">
        <div className="maintitlediv">
          <div className="ml-3">
            <h4 className="pagetitle">Rates</h4>
            <Button
              label={areFiltersVisible ? "Advanced Filter" : "Advanced Filter"}
              icon={areFiltersVisible ? "pi pi-filter-slash" : "pi pi-filter"}
              className={`p-button-text p-button-secondary shadow-btn`}
              onClick={() => {
                toggleFilterState(!areFiltersVisible);
              }}
            />
          </div>
          <div className="buttonarea">
            <div className="create-btn-group">
              {screenPermissions?.create && (
                <Button
                  label={"Create Rates"}
                  className={` p-button-raised`}
                  style={{ borderRadius: "0px" }}
                  onClick={handleCreateRateAdminRates}
                />
              )}
            </div>
            <div className="copy-excel-print-group">
              <Button
                label={"Copy"}
                className={` p-button-raised`}
                style={{ borderRadius: "0px" }}
                onClick={copyToClipboard}
              />
              <Button
                label={"Excel"}
                className={` p-button-raised`}
                style={{ borderRadius: "0px" }}
                onClick={exportRateAdminRates}
              />
              <Button
                label={"Print"}
                className={` p-button-raised`}
                style={{ borderRadius: "0px" }}
                onClick={handlePrint}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };
  const shouldRenderFullPageError = () => isPageLevelError;
  const shouldRenderComplianceRecords = () => tableData?.columns?.length > 0;
  const shouldRenderNotFoundView = () =>
    !shouldRenderComplianceRecords() && !shouldRenderFullPageError();
  
  // Build initial filters from saved state if available
  const getInitialFilters = () => {
    const defaultFilters = {
      global: { value: null },
      rateCodeTitle: { value: null, filterKey: "rateCodeTitle" },
      locationTitle: { value: null, filterKey: "locationTitle" },
      networkTitle: { value: null, filterKey: "networkTitle" },
      categoryTitle: { value: null, filterKey: "categoryTitle" },
      rateOptionTitle: { value: null, filterKey: "rateOptionTitle" },
      rateType: { value: null, filterKey: "rateType" },
      startDate: { value: null, filterKey: "startDate" },
      endDate: { value: null, filterKey: "endDate" },
      daily: { value: null, filterKey: "daily" },
      weekend: { value: null, filterKey: "weekend" },
      weekly: { value: null, filterKey: "weekly" },
      monthly: { value: null, filterKey: "monthly" },
    };

    // If we have saved filters, merge them with defaults
    if (savedTableState?.filters) {
      const savedFilters = savedTableState.filters;
      Object.keys(savedFilters).forEach(key => {
        if (defaultFilters[key]) {
          defaultFilters[key] = { ...defaultFilters[key], ...savedFilters[key] };
        }
      });
    }

    return defaultFilters;
  };

  const filters = getInitialFilters();

  const getToastProps = () => {
    let toastTitle = "";
    if (isRateAdminRatesDeleteSuccess) {
      return {
        severity: TOAST_SEVERITY.SUCCESS,
        toastTitle: "Rate deleted successfully!",
        shouldShowToast: true,
      };
    }
    if (isRateAdminRatesDeleteError) {
      return {
        severity: TOAST_SEVERITY.ERROR,
        toastTitle: "Error while deleting Rate!",
        shouldShowToast: true,
      };
    }
    if (isPrintError) {
      return {
        severity: TOAST_SEVERITY.ERROR,
        toastTitle:
          "Unable to open print window. Please check your popup blocker settings.",
        shouldShowToast: true,
      };
    }
    if (isCopy) {
      let toastTitle = "Data copied to clipboard!";
      if (!isCopy) {
        toastTitle = "Error while Data copied to clipboard!";
      }

      return {
        severity: isCopy ? TOAST_SEVERITY.SUCCESS : TOAST_SEVERITY.ERROR,
        toastTitle,
        shouldShowToast: true,
      };
    }
  };

  const rowExpansionTemplate = (data) => {
    const details = rateDetails[data.id];
    const isLoadingDetails = isLoading && pendingFetchId === data.id;

    // Show loading state while fetching details
    if (isLoadingDetails || !details) {
      return (
        <Box sx={{ backgroundColor: '#f8f9fa', padding: 0, margin: 0, '& .p-progressbar': { margin: 0 } }}>
          {renderProgressBar()}
        </Box>
      );
    }

    // Render the full ViewInlineRateAdminRates component
    return (
      <ViewInlineRateAdminRates
        formFieldValueMap={details}
        isEdit={true}
        isLoading={isLoadingDetails}
        editRateAdminRates={editRateAdminRates}
        RateCodeDD={RateCodeDD}
        locationDD={locationDD}
        vehicleCategoryDD={vehicleCategoryDD}
        RateOptionsDD={RateOptionsDD}
        RateNetworkDD={RateNetworkDD}
        // Additional props for inline view functionality
        createRateAdminRateAdjustment={createRateAdminRateAdjustment}
        TestRateAdminRates={TestRateAdminRates}
        fetchExternalNetworkDD={fetchExternalNetworkDD}
        fetchExternalRateTest={fetchExternalRateTest}
        clearExternalRateTest={clearExternalRateTest}
        rateAdjustmentlist={rateAdjustmentlist}
        RateTest={RateTest}
        ExternalNetworkDD={ExternalNetworkDD}
        ExternalRateTest={ExternalRateTest}
        isExternalRateTestSuccess={isExternalRateTestSuccess}
        isExternalRateTestError={isExternalRateTestError}
        isRateAdminRateAdjSuccess={isRateAdminRateAdjSuccess}
        isRateAdminRateAdjListSuccess={isRateAdminRateAdjListSuccess}
        isRateAdminRatesEditSuccess={isRateAdminRatesEditSuccess}
        isRateAdminRatesEditError={isRateAdminRatesEditError}
        // Disable navigation for inline view
        shouldBlockNavigation={() => {}}
        isInlineView={true}
        // Props for location-based categories
        fetchVehicleCategoryByLocation={fetchVehicleCategoryByLocation}
        vehicleCategoryByLocationDD={vehicleCategoryByLocationDD}
        isVehicleCategoryByLocationSuccess={isVehicleCategoryByLocationSuccess}
        // Props for revenue preview
        fetchRevenuePreview={fetchRevenuePreview}
        revenuePreviewData={revenuePreviewData}
        isRevenuePreviewSuccess={isRevenuePreviewSuccess}
      />
    );
  };

  const handleRowToggle = (e) => {
    console.log("Row toggle event:", e);
    console.log("e.data:", e.data);
    setExpandedRows(e.data);
    
    // Save expanded rows state for persistence
    if (onTableStateChange) {
      onTableStateChange({ expandedRows: e.data });
    }
    
    // Fetch details for newly expanded rows
    // e.data is an object with row IDs as keys when using dataKey="id"
    if (e.data) {
      const expandedIds = Object.keys(e.data);
      console.log("Expanded IDs:", expandedIds);
      
      for (const rowId of expandedIds) {
        const numericId = parseInt(rowId, 10);
        // Only fetch if we don't already have the details and not currently loading
        if (numericId && !rateDetails[numericId] && pendingFetchId !== numericId) {
          console.log("Fetching rate details for ID:", numericId);
          setPendingFetchId(numericId);
          fetchRateAdminRatesById({ id: numericId });
          // Also fetch rate adjustment details
          if (fetchRateAdminRatesRateAdjDetails) {
            fetchRateAdminRatesRateAdjDetails({ id: numericId });
          }
        }
      }
    }
  };

  const handleTableFilterChange = (filters) => {
    const locationFilterValue = filters?.locationTitle?.value;
    if (locationFilterValue && fetchVehicleCategoryByLocation) {
      fetchVehicleCategoryByLocation({ locationId: locationFilterValue });
    } else if (!locationFilterValue && fetchVehicleCategoryByLocation) {
      fetchVehicleCategoryByLocation({});
    }
  };

  const tableProps = {
    areFiltersVisible,
    columns: tableData?.columns,
    paginationInfo,
    screenPermissions,
    value: tableData?.rows,
    emptyMessage: "No Rates found.",
    filters,
    loadLazyData: fetchRateAdminRatess,
    loading: isLoading,
    showMoreActions: false,
    sortField: savedTableState?.sortField || 'id',
    initialSortOrder: savedTableState?.sortOrder,
    initialFirst: savedTableState?.first || 0,
    initialRows: savedTableState?.rows || 10,
    onTableStateChange,
    onTableFilterChange: handleTableFilterChange,
    // Clear category filter when location filter is cleared
    filterDependencies: { categoryTitle: 'locationTitle' },
    setDeleteConfirmationPopupVisibility,
    shouldOpenDeleteConfirmationPopup,
    onEditRecord: editRateAdminRatesRoute,
    onCopyRecord: copyRateAdminRatesRoute,
    onViewRecord: viewRateAdminRatesRoute,
    onDeleteRecord: deleteRateAdminRates,
    isRateAdminRatesDeleteSuccess,
    isRateAdminRatesDeleteError,
    showViewButton: false,
    showCopyButton: false,
    showDeleteButton: true,
    showEditButton: false,
    expandedRows,
    onRowToggle: handleRowToggle,
    rowExpansionTemplate,
  };
  return (
    <>
      <Container maxWidth="lg">
        <Breadcrumb list={RateAdminBreadcrumb} />

        <EzToast {...getToastProps()} />
        <div className={`containerbox ${classes.bg}`}>
          {renderPageHeaderSection()}
          <div className="mTop15">
            {shouldRenderFullPageError() && (
              <EzNotFound
                messageStyle={{ marginLeft: "-70px" }}
                bgImgUrl={getIconPath("something-broke.png")}
                message={"Oops! Something broke, we are working on a fix."}
              />
            )}
            {shouldRenderComplianceRecords() && (
              <Box className={classes.ratesTableWrapper}>
                <EzTable {...tableProps} />
              </Box>
            )}
            {shouldRenderNotFoundView() && (
              <EzNotFound
                bgImgUrl={getIconPath()}
                message={"No Rates records to show"}
              />
            )}
          </div>
        </div>
      </Container>
    </>
  );
};
export default RateAdminRates;
